import numeral from "numeral"
import { formatTime, calculateTimeAgoValue } from "../../utils/date"
import { getLabelForPrice } from "../../utils/numerals"
import type {
  DateDataTypeDefinition,
  NumberDataTypeDefinition,
  TextDataTypeDefinition,
  ValueFormatterLiteFunc,
} from "@ag-grid-community/core"

export const makePercentage = (
  value: number,
  valueFormat = "0,0.0"
): string => {
  const trailingZerosInFormat = valueFormat.split(".")[1]
  /*
   * Create maximum threshold based on requested format accuracy and limitations in the numeral library
   * We do this to avoid 2 cases
   *  1. Lib failing (returns NaN or just crashes, cant remember)
   *  2. Function returns eg. "0.00" when actual value is "0.001" but formatting doesn't allow that. In those cases the using party should resolve that to eg. "~0.00"
   */
  const threshold = Number.parseFloat(`0.0000${trailingZerosInFormat}1`)

  if (isTooSmallFloatForPercentage(value, threshold)) {
    return ""
  }

  const perc = value * 100
  if (perc > 1000000) {
    return numeral(perc).format("0.00e+0")
  }

  return numeral(perc).format(valueFormat)
}

const formatX = (value?: number) => {
  if (typeof value !== "number") return undefined

  return numeral(value).format("0,0.0")
}

const getLabelForX = (value?: number | null) => {
  if (typeof value !== "number") return "-"

  return formatX(value) + "x"
}

const isTooSmallFloatForPercentage = (value: number, threshold = 0.000001) =>
  // 0.00000001*100 is the smallest value numeral agrees to work with without retuning NaN
  !Number.isInteger(value) && Math.abs(value) < Math.max(0.00000001, threshold)

export const getLabelForPercentage = (
  value?: number | null,
  csv?: boolean,
  double?: boolean
) => {
  if (typeof value !== "number" || isTooSmallFloatForPercentage(value))
    return csv ? "" : "-"

  const prefill = value > 0 ? "+" : ""
  if (double) {
    return prefill + makePercentage(value, "0,0.00") + "%"
  }
  return prefill + makePercentage(value) + "%"
}

/*
 * getLabelForNumber - Used for formatting Tokenholders, Active users etc.
 */
export const getLabelForNumber = (value?: number | null, fallback = "-") => {
  if (value == null || isNaN(value) || value === -1) {
    return fallback
  }
  return numeral(value).format("0[.]00a")
}

const unknownFormatter: ValueFormatterLiteFunc<unknown, string> =
  function unknownFormatter({ value }) {
    return value ?? "Unknown"
  }

/** @deprecated */
export const textUnknownTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text",
  baseDataType: "text",
  valueFormatter: unknownFormatter,
  columnTypes: ["unknownColumn"],
}

/** @deprecated */
export const projectTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text-unknown",
  baseDataType: "text",
  columnTypes: ["projectColumn", "collectionColumn"],
}

/** @deprecated */
export const collectionTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text-unknown",
  baseDataType: "text",
  columnTypes: ["collectionColumn"],
}

/** @deprecated */
export const chainTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text-unknown",
  baseDataType: "text",
  columnTypes: ["chainColumn", "collectionColumn"],
}

/** @deprecated */
export const contractTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text",
  baseDataType: "text",
  columnTypes: ["contractColumn"],
}

/** @deprecated */
export const marketSectorTypeDefinition: TextDataTypeDefinition<unknown> = {
  appendColumnTypes: true,
  extendsDataType: "text-unknown",
  baseDataType: "text",
  columnTypes: ["marketSectorColumn", "collectionColumn"],
}

/** @deprecated */
export const addressTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text",
  baseDataType: "text",
  columnTypes: ["addressColumn"],
}

/** @deprecated */
export const transactionHashTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text",
  baseDataType: "text",
  columnTypes: ["transactionHashColumn"],
}

/** @deprecated */
export const urlTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text",
  baseDataType: "text",
  columnTypes: ["urlColumn"],
}

/** @deprecated */
export const percentageTypeDefinition: NumberDataTypeDefinition<unknown> = {
  extendsDataType: "number",
  baseDataType: "number",
  columnTypes: ["numberColumn"],
  valueFormatter: (params) => {
    const value =
      typeof params.value === "number" ? makePercentage(params.value) : ""

    if (value) {
      return value + "%"
    }

    return "-"
  },
}

/** @deprecated */
export const accuratePercentageTypeDefinition: NumberDataTypeDefinition<unknown> =
  {
    extendsDataType: "number",
    baseDataType: "number",
    columnTypes: ["numberColumn"],
    valueFormatter: (params) => {
      if (typeof params.value !== "number") {
        return "-"
      }

      if (params.value === 0) {
        return "0%"
      }
      // Create accurate percentage value. If is too small for even 6 digits, then show that it's rounded to really small
      const value = makePercentage(params.value, "0,0.000000")

      if (value) {
        return value + "%"
      }

      return "~0.000000%"
    },
  }

/** @deprecated */
export const currencyTypeDefinition: NumberDataTypeDefinition<string> = {
  extendsDataType: "number",
  baseDataType: "number",
  columnTypes: ["numberColumn"],
  valueFormatter: (params) => {
    const price = getLabelForPrice(params.value)
    if (params.colDef.field?.includes("_eur_")) {
      return price.replace("$", "€")
    }
    return price
  },
}

/** @deprecated */
export const accurateCurrencyTypeDefinition: NumberDataTypeDefinition<string> =
  {
    extendsDataType: "number",
    baseDataType: "number",
    columnTypes: ["numberColumn"],
    valueFormatter: (params) => {
      const price = getLabelForPrice(params.value, "accurate")
      if (params.colDef.field?.includes("_eur_")) {
        return price.replace("$", "€")
      }
      return price
    },
  }

/** @deprecated */
export const veryAccurateCurrencyTypeDefinition: NumberDataTypeDefinition<string> =
  {
    extendsDataType: "number",
    baseDataType: "number",
    columnTypes: ["numberColumn"],
    valueFormatter: (params) => {
      const price = getLabelForPrice(params.value, "veryAccurate")
      if (params.colDef.field?.includes("_eur_")) {
        return price.replace("$", "€")
      }
      return price
    },
  }

/** @deprecated */
export const ratioTypeDefinition: NumberDataTypeDefinition<unknown> = {
  extendsDataType: "number",
  baseDataType: "number",
  columnTypes: ["numberColumn"],
  valueFormatter: (params) => getLabelForX(params.value),
}

/** @deprecated */
export const numberTypeDefinition: NumberDataTypeDefinition<unknown> = {
  extendsDataType: "number",
  baseDataType: "number",
  valueFormatter: (params) => getLabelForNumber(params.value),
  columnTypes: ["numberColumn"],
}

/** @deprecated */
export const timeTypeDefinition: NumberDataTypeDefinition<unknown> = {
  extendsDataType: "number",
  baseDataType: "number",
  columnTypes: ["numberColumn"],
  valueFormatter: (params) =>
    typeof params.value === "number" ? formatTime(params.value) : "-",
}

/** @deprecated */
export const trendTypeDefinition: NumberDataTypeDefinition<unknown> = {
  extendsDataType: "number",
  baseDataType: "number",
  valueFormatter: (params) => {
    return typeof params.value === "number"
      ? getLabelForPercentage(params.value)
      : "-"
  },
  columnTypes: ["trendingColumn"],
}

/** @deprecated */
export const trendInverseTypeDefinition: NumberDataTypeDefinition<unknown> = {
  extendsDataType: "number",
  baseDataType: "number",
  valueFormatter: (params) => {
    return typeof params.value === "number"
      ? getLabelForPercentage(params.value)
      : "-"
  },
  columnTypes: ["trendingInverseColumn"],
}

/** @deprecated */
export const datetimeTypeDefinition: DateDataTypeDefinition<unknown> = {
  extendsDataType: "date",
  baseDataType: "date",
  columnTypes: ["dateTimeColumn"],
  valueFormatter: (params) => {
    try {
      /*
       * AG Grid suspected bug here
       * SSRM Mode: value is Date
       * Client side mode: value is string
       *
       * We mainly use SSRM, but exporting has to use client side, so both need to be supported.
       */
      let value = params.value as Date | null | string

      if (typeof value === "string") {
        if (!value) {
          throw "Missing date"
        }

        value = new Date(value)
      }

      if (value) {
        const day = value.getUTCDate().toString().padStart(2, "0")
        const month = (value.getUTCMonth() + 1).toString().padStart(2, "0")
        const year = value.getUTCFullYear().toString()
        const hours = value.getUTCHours().toString().padStart(2, "0")
        const minutes = value.getUTCMinutes().toString().padStart(2, "0")
        const seconds = value.getUTCSeconds().toString().padStart(2, "0")

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`
      }
      throw "Invalid date"
    } catch {
      return "N/A"
    }
  },
}

// Not sure why we need custom date since it's a base type, but filtering wont work without it so we do need it
/** @deprecated */
export const dateTypeDefinition: DateDataTypeDefinition<unknown> = {
  extendsDataType: "date",
  baseDataType: "date",
  columnTypes: ["dateColumn"],
}

/** @deprecated */
export const timeAgoTypeDefinition: DateDataTypeDefinition<unknown> = {
  extendsDataType: "date",
  baseDataType: "date",
  columnTypes: ["dateTimeColumn"],
  valueFormatter(params) {
    // TODO: whoever knows this column, check if it's correct to not show 0 and put a comment here that this is the business logic for timeAgo
    if (!params.value || isNaN(params.value as unknown as number)) {
      return "N/A"
    }

    return calculateTimeAgoValue(params.value.getTime())
  },
}

/** @deprecated */
export const cohortTypeDefinition: NumberDataTypeDefinition<unknown> = {
  extendsDataType: "percentage",
  baseDataType: "number",
  columnTypes: ["cohortColumn"],
}

/** @deprecated */
export const currencyColorizedTypeDefinition: NumberDataTypeDefinition<unknown> =
  {
    extendsDataType: "currency",
    baseDataType: "number",
    columnTypes: ["numberColumn", "currencyColorizedColumn"],
  }

/** @deprecated */
export const buyAndSellTypeDefinition: TextDataTypeDefinition<unknown> = {
  extendsDataType: "text",
  baseDataType: "text",
  columnTypes: ["collectionColumn", "buyAndSellColumn"],
}

/** @deprecated */
export const publicDataTypes = {
  project: projectTypeDefinition as TextDataTypeDefinition,
  chain: chainTypeDefinition as TextDataTypeDefinition,
  contract: contractTypeDefinition as TextDataTypeDefinition,
  marketSector: marketSectorTypeDefinition as TextDataTypeDefinition,
  "text-unknown": textUnknownTypeDefinition as TextDataTypeDefinition,
  url: urlTypeDefinition as TextDataTypeDefinition,
  address: addressTypeDefinition as TextDataTypeDefinition,
  transactionHash: transactionHashTypeDefinition as TextDataTypeDefinition,
  percentage: percentageTypeDefinition as NumberDataTypeDefinition,
  accuratePercentage:
    accuratePercentageTypeDefinition as NumberDataTypeDefinition,
  // One decimal place for currency
  currency: currencyTypeDefinition as NumberDataTypeDefinition,
  // Four decimal accuracy, poor naming due to lack of hindsight
  lessAccurateCurrency:
    accurateCurrencyTypeDefinition as NumberDataTypeDefinition,
  // Six decimal accuracy
  accurateCurrency:
    veryAccurateCurrencyTypeDefinition as NumberDataTypeDefinition,
  ratio: ratioTypeDefinition as NumberDataTypeDefinition,
  number: numberTypeDefinition as NumberDataTypeDefinition,
  trend: trendTypeDefinition as NumberDataTypeDefinition,
  trendInverse: trendInverseTypeDefinition as NumberDataTypeDefinition,
  timeAgo: timeAgoTypeDefinition as DateDataTypeDefinition,
  time: timeTypeDefinition as NumberDataTypeDefinition,
  collection: collectionTypeDefinition as TextDataTypeDefinition,
  dateTime: datetimeTypeDefinition as DateDataTypeDefinition,
  date: dateTypeDefinition as DateDataTypeDefinition,
  cohort: cohortTypeDefinition as NumberDataTypeDefinition,
  currencyColorized:
    currencyColorizedTypeDefinition as NumberDataTypeDefinition,
  buyAndSell: buyAndSellTypeDefinition as TextDataTypeDefinition,
}

/** @deprecated */
export type DataGridDataType = keyof typeof publicDataTypes
